/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { fromUtf8ToBase64, performance } from "@fluid-internal/client-utils";
import { assert } from "@fluidframework/core-utils/internal";
import { GenericNetworkError, NonRetryableError, } from "@fluidframework/driver-utils/internal";
import { CorrelationIdHeaderName, DriverVersionHeaderName, RestLessClient, getAuthorizationTokenFromCredentials, } from "@fluidframework/server-services-client";
import { PerformanceEvent, numberFromString, } from "@fluidframework/telemetry-utils/internal";
import fetch from "cross-fetch";
import safeStringify from "json-stringify-safe";
import { RouterliciousErrorTypes, throwR11sNetworkError } from "./errorUtils.js";
import { pkgVersion as driverVersion } from "./packageVersion.js";
import { addOrUpdateQueryParams } from "./queryStringUtils.js";
import { RestWrapper } from "./restWrapperBase.js";
const buildRequestUrl = (requestConfig) => requestConfig.baseURL !== undefined
    ? `${requestConfig.baseURL ?? ""}${requestConfig.url ?? ""}`
    : requestConfig.url ?? "";
const axiosBuildRequestInitConfig = (requestConfig) => {
    const requestInit = {
        method: requestConfig.method,
        // NOTE: I believe that although the Axios type permits non-string values in the header, here we are
        // guaranteed the requestConfig only has string values in its header.
        headers: requestConfig.headers,
        body: requestConfig.data,
    };
    return requestInit;
};
/**
 * A utility function to create a Routerlicious response without any additional props as we might not have them always.
 * @param content - Response which is equivalent to content.
 * @returns A Routerlicious response without any extra props.
 */
export function createR11sResponseFromContent(content) {
    return {
        content,
        headers: new Map(),
        propsToLog: {},
        requestUrl: "",
    };
}
function headersToMap(headers) {
    const newHeaders = new Map();
    for (const [key, value] of headers.entries()) {
        newHeaders.set(key, value);
    }
    return newHeaders;
}
export function getPropsToLogFromResponse(headers) {
    // We rename headers so that otel doesn't scrub them away. Otel doesn't allow
    // certain characters in headers including '-'
    const headersToLog = [
        { headerName: CorrelationIdHeaderName, logName: "requestCorrelationId" },
        { headerName: "content-encoding", logName: "contentEncoding" },
        { headerName: "content-type", logName: "contentType" },
    ];
    const additionalProps = {
        contentsize: numberFromString(headers.get("content-length")),
    };
    headersToLog.forEach((header) => {
        const headerValue = headers.get(header.headerName);
        if (headerValue !== undefined && headerValue !== null) {
            additionalProps[header.logName] = headerValue;
        }
    });
    return additionalProps;
}
class RouterliciousRestWrapper extends RestWrapper {
    constructor(logger, rateLimiter, fetchRefreshedToken, getAuthorizationHeader, useRestLess, baseurl, tokenP, defaultQueryString = {}) {
        super(baseurl, defaultQueryString);
        this.rateLimiter = rateLimiter;
        this.fetchRefreshedToken = fetchRefreshedToken;
        this.getAuthorizationHeader = getAuthorizationHeader;
        this.useRestLess = useRestLess;
        this.tokenP = tokenP;
        this.restLess = new RestLessClient();
        /**
         * A locally maintained map which saves the number of retries for any REST api call made through restWrapper.
         * It uses the href of the request url as a key against which it saves the retry counts. Retries are only counted in case of failures.
         * This feature is added to enable FRS to have more telemetry insights into whether the requests are being retried from same client or multiple.
         */
        this.retryCounter = new Map();
    }
    async request(requestConfig, statusCode, canRetry = true) {
        if (requestConfig.params) {
            // delete the retry param, if any. We do this to ensure there is no retry added by any of callers, which would conflict with the one we maintain here in the retryCounter map.
            delete requestConfig.params.retry;
        }
        // Build the complete request url including baseUrl, url and query params. (all except 'retry' query param)
        let completeRequestUrl = addOrUpdateQueryParams(buildRequestUrl(requestConfig), requestConfig.params);
        // Check whether this request has been made before or if it is a retry.
        // requestKey is built using the HTTP method appended with the complete URL ommitting the 'retry' param
        const requestKey = `${requestConfig.method ?? ""}|${completeRequestUrl.href}`;
        const requestRetryCount = this.retryCounter.get(requestKey);
        if (requestRetryCount) {
            completeRequestUrl = addOrUpdateQueryParams(completeRequestUrl, {
                retry: requestRetryCount,
            });
        }
        const config = {
            ...requestConfig,
            headers: await this.generateHeaders(requestConfig.headers),
        };
        const translatedConfig = this.useRestLess ? this.restLess.translate(config) : config;
        const fetchRequestConfig = axiosBuildRequestInitConfig(translatedConfig);
        const res = await this.rateLimiter.schedule(async () => {
            const perfStart = performance.now();
            const result = await fetch(completeRequestUrl, fetchRequestConfig).catch(async (error) => {
                // on failure, add the request entry into the retryCounter map to count the subsequent retries, if any
                this.retryCounter.set(requestKey, requestRetryCount ? requestRetryCount + 1 : 1);
                // Browser Fetch throws a TypeError on network error, `node-fetch` throws a FetchError
                const isNetworkError = ["TypeError", "FetchError"].includes(error?.name);
                const errorMessage = isNetworkError
                    ? `NetworkError: ${error.message}`
                    : safeStringify(error);
                // If a service is temporarily down or a browser resource limit is reached, RestWrapper will throw
                // a network error with no status code (e.g. err:ERR_CONN_REFUSED or err:ERR_FAILED) and
                // the error message will start with NetworkError as defined in restWrapper.ts
                // If there exists a self-signed SSL certificates error, throw a NonRetryableError
                // TODO: instead of relying on string matching, filter error based on the error code like we do for websocket connections
                const err = errorMessage.includes("failed, reason: self signed certificate")
                    ? new NonRetryableError(errorMessage, RouterliciousErrorTypes.sslCertError, {
                        driverVersion,
                        retryCount: requestRetryCount,
                    })
                    : new GenericNetworkError(errorMessage, errorMessage.startsWith("NetworkError"), {
                        driverVersion,
                        retryCount: requestRetryCount,
                    });
                throw err;
            });
            return {
                response: result,
                duration: performance.now() - perfStart,
            };
        });
        const response = res.response;
        let start = performance.now();
        const text = await response.text();
        const receiveContentTime = performance.now() - start;
        const bodySize = text.length;
        start = performance.now();
        const responseBody = response.headers
            .get("content-type")
            ?.includes("application/json")
            ? JSON.parse(text)
            : text;
        const parseTime = performance.now() - start;
        // Success
        if (response.ok || response.status === statusCode) {
            // on successful response, remove the entry from the retryCounter map
            this.retryCounter.delete(requestKey);
            const result = responseBody;
            const headers = headersToMap(response.headers);
            return {
                content: result,
                headers,
                requestUrl: completeRequestUrl.href,
                propsToLog: {
                    ...getPropsToLogFromResponse(headers),
                    bodySize,
                    receiveContentTime,
                    parseTime,
                    fetchTime: res.duration,
                },
            };
        }
        // Failure
        // on failure, add the request entry into the retryCounter map to count the subsequent retries
        this.retryCounter.set(requestKey, requestRetryCount ? requestRetryCount + 1 : 1);
        if (response.status === 401 && canRetry) {
            // Refresh Authorization header and retry once
            this.token = await this.fetchRefreshedToken(true /* refreshToken */);
            return this.request({ ...config }, statusCode, false);
        }
        if (response.status === 429 && responseBody?.retryAfter > 0) {
            // Retry based on retryAfter[Seconds]
            return new Promise((resolve, reject) => setTimeout(() => {
                // use the original request URL without the retryCount appended
                this.request({ ...config }, statusCode)
                    .then(resolve)
                    .catch(reject);
            }, responseBody.retryAfter * 1000));
        }
        const responseSummary = responseBody !== undefined
            ? typeof responseBody === "string"
                ? responseBody
                : safeStringify(responseBody)
            : response.statusText;
        throwR11sNetworkError(`R11s fetch error: ${responseSummary}`, response.status, responseBody?.retryAfter);
    }
    async generateHeaders(requestHeaders) {
        const token = await this.getToken();
        assert(token !== undefined, 0x679 /* token should be present */);
        const headers = {
            ...requestHeaders,
            [DriverVersionHeaderName]: driverVersion,
            // NOTE: If this.authorizationHeader is undefined, should "Authorization" be removed entirely?
            Authorization: this.getAuthorizationHeader(token),
        };
        return headers;
    }
    async getToken() {
        if (this.token !== undefined) {
            return this.token;
        }
        const token = await (this.tokenP ?? this.fetchRefreshedToken());
        this.setToken(token);
        this.tokenP = undefined;
        return token;
    }
    setToken(token) {
        this.token = token;
    }
}
export class RouterliciousStorageRestWrapper extends RouterliciousRestWrapper {
    constructor(logger, rateLimiter, fetchToken, getAuthorizationHeader, useRestLess, baseurl, initialTokenP, defaultQueryString = {}) {
        super(logger, rateLimiter, fetchToken, getAuthorizationHeader, useRestLess, baseurl, initialTokenP, defaultQueryString);
    }
    static load(tenantId, tokenFetcher, logger, rateLimiter, useRestLess, baseurl, initialTokenP) {
        const defaultQueryString = {
            token: `${fromUtf8ToBase64(tenantId)}`,
        };
        const getAuthorizationHeader = (token) => {
            const credentials = {
                password: token.jwt,
                user: tenantId,
            };
            return getAuthorizationTokenFromCredentials(credentials);
        };
        const restWrapper = new RouterliciousStorageRestWrapper(logger, rateLimiter, tokenFetcher, getAuthorizationHeader, useRestLess, baseurl, initialTokenP, defaultQueryString);
        return restWrapper;
    }
}
export class RouterliciousOrdererRestWrapper extends RouterliciousRestWrapper {
    constructor(logger, rateLimiter, fetchToken, getAuthorizationHeader, useRestLess, baseurl, initialTokenP, defaultQueryString = {}) {
        super(logger, rateLimiter, fetchToken, getAuthorizationHeader, useRestLess, baseurl, initialTokenP, defaultQueryString);
    }
    static load(tokenFetcher, logger, rateLimiter, useRestLess, baseurl, initialTokenP) {
        const getAuthorizationHeader = (token) => {
            return `Basic ${token.jwt}`;
        };
        const restWrapper = new RouterliciousOrdererRestWrapper(logger, rateLimiter, tokenFetcher, getAuthorizationHeader, useRestLess, baseurl, initialTokenP);
        return restWrapper;
    }
}
export function toInstrumentedR11sOrdererTokenFetcher(tenantId, documentId, tokenProvider, logger) {
    const fetchOrdererToken = async (refreshToken) => {
        return PerformanceEvent.timedExecAsync(logger, {
            eventName: "FetchOrdererToken",
            docId: documentId,
        }, async () => {
            const ordererToken = await tokenProvider.fetchOrdererToken(tenantId, documentId, refreshToken);
            return ordererToken;
        });
    };
    return fetchOrdererToken;
}
export function toInstrumentedR11sStorageTokenFetcher(tenantId, documentId, tokenProvider, logger) {
    const fetchStorageToken = async (refreshToken) => {
        return PerformanceEvent.timedExecAsync(logger, {
            eventName: "FetchStorageToken",
            docId: documentId,
        }, async () => {
            // Craft credentials using tenant id and token
            const storageToken = await tokenProvider.fetchStorageToken(tenantId, documentId, refreshToken);
            return storageToken;
        });
    };
    return fetchStorageToken;
}
//# sourceMappingURL=restWrapper.js.map